// The tab bar and view registry. Lazily imports src/views/<tab>/index.js on
// activation; a tab with no view module yet renders "Coming soon" rather than
// crashing. The active tab is remembered in location.hash so a reload or a shared
// link lands on the same view.

import { el } from "./dom.js";

// The Syntaxeum Console (docs/console-plan.md): Today, Delivery and Money are implemented; Work,
// Schools, Reliability and Security are placeholders that say what they will show. The four
// pre-console tabs stay after them until their replacements exist (CONTRACT.md describes them):
// `board` is the one screen, `pipeline` is the lane-train map ("Trains": the owner watches it in real
// time, 2026-09-30), `more` is Backlog/Deferred/Segments, `tenders` is the procurement tracker.
export const TAB_ORDER = ["today", "delivery", "work", "schools", "reliability", "security", "money", "board", "pipeline", "more", "tenders"];

const TAB_LABELS = {
  today: "Today",
  delivery: "Delivery",
  work: "Work",
  schools: "Schools",
  reliability: "Reliability",
  security: "Security",
  money: "Money",
  board: "Old board",
  pipeline: "Trains",
  more: "Backlog",
  tenders: "Tenders",
};

let currentUnmount = null;
let activationToken = 0;

function tabFromHash() {
  const raw = (location.hash || "").replace(/^#/, "");
  return TAB_ORDER.includes(raw) ? raw : TAB_ORDER[0];
}

async function loadView(tab) {
  // Dynamic import with a literal-ish template so Vite can statically analyse the
  // glob of possible modules; a tab with no view module rejects and we fall back.
  return import(`../views/${tab}/index.js`);
}

async function activate(tab, viewRoot) {
  // A slower-resolving activation (e.g. a view whose dynamic import errors and
  // falls through a rejection path) can otherwise finish after a newer one and
  // stomp on/append to whatever the newer activation already rendered. Each
  // call gets a token; only the most recent one is allowed to touch the DOM
  // or `currentUnmount` once its async work resolves.
  const myToken = ++activationToken;

  if (typeof currentUnmount === "function") {
    try {
      currentUnmount();
    } catch {
      /* a broken unmount must not block switching tabs */
    }
    currentUnmount = null;
  }
  viewRoot.replaceChildren();

  let mod;
  try {
    mod = await loadView(tab);
  } catch {
    mod = null;
  }
  if (myToken !== activationToken) return; // superseded while awaiting the import

  if (!mod || typeof mod.mount !== "function") {
    viewRoot.append(el("div", { class: "panel" }, "Coming soon"));
    return;
  }

  try {
    // Views are not logged (D58 closed: view logging dropped, Mark, 2026-10-14).
    currentUnmount = mod.mount(viewRoot);
  } catch {
    if (myToken !== activationToken) return;
    viewRoot.replaceChildren();
    viewRoot.append(el("div", { class: "panel" }, "Coming soon"));
  }
}

/**
 * Mount the tab bar into `barRoot` and the active view into `viewRoot`.
 * Returns a teardown function that removes the hashchange listener this
 * installs on `window` — callers that only ever mount the shell once (the
 * real app) can ignore it, but anything that calls `mountTabs` more than
 * once in the same window (tests, hot reload) must call it to avoid
 * leaking a listener that holds onto a stale, detached `viewRoot`.
 */
export function mountTabs(barRoot, viewRoot) {
  function render() {
    const active = tabFromHash();
    barRoot.replaceChildren();
    for (const tab of TAB_ORDER) {
      barRoot.append(
        el(
          "button",
          {
            role: "tab",
            "aria-selected": String(tab === active),
            onClick: () => {
              if (location.hash.replace(/^#/, "") === tab) {
                activate(tab, viewRoot);
              } else {
                location.hash = tab;
              }
            },
          },
          TAB_LABELS[tab] || tab
        )
      );
    }
    activate(active, viewRoot);
  }

  window.addEventListener("hashchange", render);
  render();

  return function unmountTabs() {
    window.removeEventListener("hashchange", render);
  };
}
